/**
 * Home's Waiting on you (form-request-reskin, mockup form-request-flow.html Frames 5–6 + the
 * collapsed inset; web-account-pages D12, docs/mockups/telify-web/complete-booking.html). It draws
 * the dashboard's `waitingOnYou` list as given: held bookings (soonest deadline first), the sent
 * forms (oldest first), then approved requests to pay. Mounted above the Home hero:
 *   1 thing    → the full "doorbell" card                 → its own page
 *   2+ things  → one grouped card, a row per thing, the count counting everything
 *   3+ forms   → folded into one summary (the whole card when only forms wait, else a row in the
 *                group) that opens in place into a row per form; Show less folds it back (D5)
 *   nothing    → renders nothing
 *
 * A held booking's row reads its offering and "Finish by" the deadline, its button the Telegram
 * message's own (Complete booking, Pay $140, Complete Intake or Register). The alert navigates
 * nowhere itself: each side passes where a form and a booking open (shared-client-screens D2).
 * Meta is the sent date only for a form; no fabricated duration estimates.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { animate, useReducedMotion } from 'framer-motion';
import { ChevronRightIcon } from '@/pages/BookingHubPage/sections/icons';
import type { WaitingOnYouItem } from '@/types/dashboard';
import { APPROVED_PAYMENT, formatFinishByDay } from '@/utils/waitingBooking';
import styles from './PendingFormsAlert.module.css';

const SENT_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const formatSent = (iso: string) => SENT_FMT.format(new Date(iso));

/** The card's height follows the tap on a spring; a tap mid-travel starts from where it is. */
const FOLD_SPRING = { type: 'spring', stiffness: 420, damping: 40 } as const;

/** From this many forms on, they fold into one summary (Gabriel 2026-10-22). */
const SUMMARY_FROM = 3;

/** Document glyph for a single request (matches the Profile pending-forms icon). */
function FormIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="9" y1="13" x2="15" y2="13" />
      <line x1="9" y1="17" x2="13" y2="17" />
    </svg>
  );
}

/** Clipboard glyph for the 3+ forms summary (mockup inset). */
function ClipboardIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <line x1="9" y1="12" x2="15" y2="12" />
      <line x1="9" y1="16" x2="13" y2="16" />
    </svg>
  );
}

/** Clock glyph for a held booking: it has a deadline (complete-booking.html). */
function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

/** Card glyph for an approved request still to pay. */
function CardIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
    </svg>
  );
}

/** Up chevron for Show less. */
function ChevronUpIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 15l-6-6-6 6" />
    </svg>
  );
}

export interface PendingFormsAlertProps {
  /** The dashboard's `waitingOnYou`, drawn in the order given. */
  items: WaitingOnYouItem[];
  /** Opens a request's form: the app's form page, or the website's. */
  onOpenForm: (requestToken: string) => void;
  /** Opens a booking's Complete step or its pay page: the app's, or the website's. */
  onOpenBooking: (bookingId: string, target: 'complete' | 'pay') => void;
}

type Handlers = Pick<PendingFormsAlertProps, 'onOpenForm' | 'onOpenBooking'>;

/** What one waiting thing shows and where it goes. */
function lineOf(item: WaitingOnYouItem, on: Handlers): { key: string; icon: ReactNode; name: string; meta: string; cta: string; open: () => void } {
  if (item.kind === 'form') {
    return {
      key: `form:${item.requestToken}`,
      icon: <FormIcon />,
      name: item.formName,
      meta: `Sent ${formatSent(item.sentAt)}`,
      cta: 'Complete',
      open: () => on.onOpenForm(item.requestToken),
    };
  }
  if (item.kind === 'held') {
    return {
      key: `held:${item.bookingId}`,
      icon: <ClockIcon />,
      name: item.offeringName,
      meta: `Finish by ${formatFinishByDay(item.completeBy)}`,
      cta: item.label,
      open: () => on.onOpenBooking(item.bookingId, item.target),
    };
  }
  return {
    key: `pay:${item.bookingId}`,
    icon: <CardIcon />,
    name: item.offeringName,
    // The website's sentence, split over the row's two lines: the name, then the rest of it.
    meta: APPROVED_PAYMENT.after,
    cta: APPROVED_PAYMENT.action,
    open: () => on.onOpenBooking(item.bookingId, 'pay'),
  };
}

/** One tappable row of the grouped card. */
function Row({ line }: { line: ReturnType<typeof lineOf> }) {
  return (
    <button type="button" className={styles.row} onClick={line.open} data-testid="pending-form-row">
      <div className={styles.ico}>{line.icon}</div>
      <div className={styles.main}>
        <div className={styles.name}>{line.name}</div>
        <div className={styles.meta}>{line.meta}</div>
      </div>
      <span className={styles.cta}>
        {line.cta}
        <ChevronRightIcon />
      </span>
    </button>
  );
}

/**
 * The grouped card. Three or more forms fold into one summary, which opens in place into a row
 * per form and folds back with Show less. The card's own height springs between the two,
 * measured from what is on screen at the tap, so a tap mid-travel reverses.
 */
function WaitingGroup({ items, on }: { items: WaitingOnYouItem[]; on: Handlers }) {
  const reduced = useReducedMotion();
  const [open, setOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const fromHeight = useRef<number | null>(null);

  const toggle = (next: boolean) => {
    fromHeight.current = cardRef.current?.getBoundingClientRect().height ?? null;
    setOpen(next);
  };

  useLayoutEffect(() => {
    const el = cardRef.current;
    const from = fromHeight.current;
    fromHeight.current = null;
    if (!el || from === null) return;
    el.style.height = '';
    const to = el.getBoundingClientRect().height;
    if (reduced || from === to) return;
    let live = true;
    const controls = animate(el, { height: [from, to] }, FOLD_SPRING);
    void controls.then(() => {
      if (live) el.style.height = '';
    });
    return () => {
      live = false;
      controls.stop();
    };
  }, [open, reduced]);

  const forms = items.filter((item) => item.kind === 'form');
  const folded = forms.length >= SUMMARY_FROM;
  const oldestSent = forms[0] ? `Oldest sent ${formatSent(forms[0].sentAt)}` : '';

  // Only forms waiting, folded: the whole card is the summary.
  if (folded && !open && forms.length === items.length) {
    return (
      <div ref={cardRef} className={`${styles.alertCard} ${styles.group}`} data-testid="pending-forms-alert">
        <button type="button" className={styles.summary} onClick={() => toggle(true)}>
          <div className={styles.ico}>
            <ClipboardIcon />
          </div>
          <div className={styles.main}>
            <div className={styles.kicker}>Waiting on you</div>
            <div className={styles.name}>{forms.length} forms to fill out</div>
            <div className={styles.meta}>{oldestSent}</div>
          </div>
          <span className={styles.cta}>
            View forms
            <ChevronRightIcon />
          </span>
        </button>
      </div>
    );
  }

  const rows: ReactNode[] = [];
  let formsPlaced = false;
  for (const item of items) {
    if (item.kind === 'form' && folded) {
      if (formsPlaced) continue;
      formsPlaced = true;
      if (open) {
        for (const form of forms) {
          const line = lineOf(form, on);
          rows.push(<Row key={line.key} line={line} />);
        }
      } else {
        rows.push(
          <Row
            key="forms-summary"
            line={{ key: 'forms-summary', icon: <ClipboardIcon />, name: `${forms.length} forms to fill out`, meta: oldestSent, cta: 'View forms', open: () => toggle(true) }}
          />,
        );
      }
      continue;
    }
    const line = lineOf(item, on);
    rows.push(<Row key={line.key} line={line} />);
  }

  return (
    <div ref={cardRef} className={`${styles.alertCard} ${styles.group}`} data-testid="pending-forms-alert">
      <div className={styles.groupHead}>
        <span className={styles.kicker}>Waiting on you</span>
        <span className={styles.groupCount}>{items.length}</span>
        {folded && open && (
          <button type="button" className={`${styles.cta} ${styles.less}`} onClick={() => toggle(false)}>
            Show less
            <ChevronUpIcon />
          </button>
        )}
      </div>
      {rows}
    </div>
  );
}

export function PendingFormsAlert({ items, onOpenForm, onOpenBooking }: PendingFormsAlertProps) {
  const on = { onOpenForm, onOpenBooking };
  if (items.length === 0) return null;
  if (items.length > 1) return <WaitingGroup items={items} on={on} />;

  // 1 → the full doorbell card straight into its page.
  const line = lineOf(items[0]!, on);
  return (
    <button type="button" className={styles.alertCard} data-testid="pending-forms-alert" onClick={line.open}>
      <div className={styles.ico}>{line.icon}</div>
      <div className={styles.main}>
        <div className={styles.kicker}>Waiting on you</div>
        <div className={styles.name}>{line.name}</div>
        <div className={styles.meta}>{line.meta}</div>
      </div>
      <span className={styles.cta}>
        {line.cta}
        <ChevronRightIcon />
      </span>
    </button>
  );
}
